import React, { useState, useEffect, useMemo } from "react";
import LedgersService from "../../../services/AccountingServices/LedgerService";
import LedgerTypeService from "../../../services/AccountingServices/LedgerTypeService";
import ResponsiveDataManagement from "../../../Components/Common/ResponsiveManagementLayout";
import TableSearch from "../../../Components/Common/TableSearch";
import LedgerForm from "./LedgerForm";
import DeleteModal from "../../../Components/Common/DeleteModal";
import { Badge, Row, Col, Button } from "reactstrap";
import Select from "react-select";
import toast from "react-hot-toast";

const Ledgers = () => {
  const [allLedgers, setAllLedgers] = useState([]);
  const [ledgerTypes, setLedgerTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLedgerTypeId, setSelectedLedgerTypeId] = useState(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchLedgers(), fetchLedgerTypes()]);
    } catch (error) {
      console.error("Error in initial fetch:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLedgers = async () => {
    try {
      const response = await LedgersService.getList();
      const data = Array.isArray(response) ? response : response?.items || [];
      setAllLedgers(data);
    } catch (error) {
      console.error("Error fetching ledgers:", error);
      toast.error("Failed to fetch ledgers");
    }
  };

  const fetchLedgerTypes = async () => {
    try {
      const response = await LedgerTypeService.getList();
      const types = Array.isArray(response) ? response : response?.items || [];
      const formattedTypes = types.map((type) => ({
        value: type.id,
        label: `${type.name} (${type.category})`,
      }));
      setLedgerTypes(formattedTypes);
    } catch (error) {
      console.error("Error fetching ledger types:", error);
    }
  };

  const filteredLedgers = useMemo(() => {
    return allLedgers.filter((ledger) => {
      const matchesType = selectedLedgerTypeId 
        ? ledger.ledgerTypeId === selectedLedgerTypeId 
        : true;
      
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = searchQuery === "" || 
        ledger.name?.toLowerCase().includes(searchLower) ||
        ledger.code?.toLowerCase().includes(searchLower) ||
        ledger.ledgerTypeName?.toLowerCase().includes(searchLower) ||
        ledger.ledgerTypeCategory?.toLowerCase().includes(searchLower);

      return matchesType && matchesSearch;
    });
  }, [allLedgers, searchQuery, selectedLedgerTypeId]);

  const toggleForm = () => {
    if (isFormOpen) setSelectedItem(null);
    setIsFormOpen(!isFormOpen);
  };

  const toggleDelete = () => setIsDeleteOpen(!isDeleteOpen);

  const handleAdd = () => {
    setSelectedItem(null);
    setIsFormOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setIsDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await LedgersService.delete(selectedItem.id);
      toast.success("Ledger deleted successfully");
      fetchLedgers();
      toggleDelete();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete ledger");
    }
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      width: "70px",
    },
    {
      name: "Code",
      selector: (row) => row.code,
      sortable: true,
      width: "100px",
      cell: (row) => <span className="fs-12">{row.code}</span>,
    },
    {
      name: "Ledger Name",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div>
          <div className="fw-semibold text-primary">{row.name}</div>
          {row.parentLedgerName && (
            <small className="text-muted">Parent: {row.parentLedgerName}</small>
          )}
        </div>
      ),
    },
    {
      name: "Type",
      selector: (row) => row.ledgerTypeName,
      sortable: true,
      cell: (row) => (
        <div>
          <span className="fw-medium">{row.ledgerTypeName}</span>
          <br />
          <small className="text-muted text-uppercase">{row.ledgerTypeCategory}</small>
        </div>
      ),
    },
    {
      name: "Category",
      selector: (row) => row.ledgerTypeCategory,
      sortable: true,
      cell: (row) => {
        const colors = {
          ASSETS: "success",
          LIABILITIES: "danger",
          INCOME: "info",
          EXPENSES: "warning",
          EQUITY: "primary",
        };
        return (
          <Badge color={colors[row.ledgerTypeCategory] || "secondary"} pill className="text-uppercase px-3 py-1">
            {row.ledgerTypeCategory}
          </Badge>
        );
      },
    },
  ];

  const stats = [
    {
      title: "Total Ledgers",
      value: allLedgers.length,
      color: "#4A90E2",
      icon: "bx bx-book-content",
    },
    {
      title: "Assets",
      value: allLedgers.filter(l => l.ledgerTypeCategory === "ASSETS").length,
      color: "#27AE60",
      icon: "bx bx-trending-up",
    },
    {
      title: "Liabilities",
      value: allLedgers.filter(l => l.ledgerTypeCategory === "LIABILITIES").length,
      color: "#E74C3C",
      icon: "bx bx-trending-down",
    },
  ];

  return (
    <div className="page-content p-0">
      <ResponsiveDataManagement
        title="Ledgers"
        pageTitle="Accounting Setup"
        data={filteredLedgers}
        columns={columns}
        stats={stats}
        onAdd={null}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        loading={loading}
        showSearch={false}
        searchKeys={["name", "code", "ledgerTypeName", "ledgerTypeCategory"]}
        emptyStateMessage="No ledgers found"
        customHeaderContent={
          <Row className="mb-3">
            <Col md={4}>
              <h5 className="mb-1 fw-bold text-dark">Ledger List</h5>
              <p className="text-muted small mb-0">Manage and view all ledger accounts</p>
            </Col>
            <Col md={8}>
              <div className="d-flex justify-content-end align-items-center gap-2">
                <TableSearch
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search ledgers..."
                  style={{ minWidth: "250px" }}
                />
                <div style={{ minWidth: "220px" }}>
                  <Select
                    options={ledgerTypes}
                    value={ledgerTypes.find((opt) => opt.value === selectedLedgerTypeId)}
                    onChange={(opt) => setSelectedLedgerTypeId(opt?.value || null)}
                    placeholder="All Types"
                    isClearable={true}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (base) => ({
                        ...base,
                        minHeight: "38px",
                        borderRadius: "8px",
                        borderColor: "#dee2e6",
                        "&:hover": { borderColor: "#86b7fe" },
                      }),
                    }}
                  />
                </div>
                <Button
                  color="success"
                  onClick={handleAdd}
                  className="d-flex align-items-center gap-1"
                  style={{ whiteSpace: "nowrap" }}
                >
                  <i className="bx bx-plus fs-16"></i> Add Ledger
                </Button>
              </div>
            </Col>
          </Row>
        }
      />

      <LedgerForm
        isOpen={isFormOpen}
        toggle={toggleForm}
        selectedItem={selectedItem}
        refetch={fetchLedgers}
      />

      <DeleteModal
        show={isDeleteOpen}
        onCloseClick={toggleDelete}
        onDeleteClick={handleDeleteConfirm}
        itemName={selectedItem?.name}
      />
    </div>
  );
};

export default Ledgers;
